import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import {
  PUT_USER,
  SEND_PHONE_VERIFICATION_CODE,
  CONFIRM_PHONE_VERIFICATION_CODE,
} from "../../../../api/user";
import { updateUserDetails } from "../../../../redux/auth/actions";
import { toE164, isValidPhone, PHONE_ERROR } from "../../../../utils/phone";

const RESEND_SECONDS = 45;

const ERROR_COPY = {
  invalid_phone: PHONE_ERROR,
  no_phone: "Add a phone number first, then tap Verify.",
  already_verified: "This number is already verified.",
  cooldown: "Please wait a moment before requesting another code.",
  rate_limited: "Too many codes requested. Please try again in an hour.",
  not_configured: "Text messaging isn't available right now. Please try again later.",
  send_failed: "We couldn't send a text to that number. Check it and try again.",
  no_code: "Request a new code and try again.",
  expired: "That code expired. Request a new one.",
  too_many_attempts: "Too many wrong attempts. Request a new code.",
  wrong_code: "That code isn't right. Check it and try again.",
};

const errorMessage = (err) => {
  const data = err?.response?.data;
  if (data?.error === "wrong_code" && typeof data.attemptsLeft === "number") {
    return `That code isn't right. ${data.attemptsLeft} ${
      data.attemptsLeft === 1 ? "try" : "tries"
    } left.`;
  }
  return ERROR_COPY[data?.error] || data?.message || "Something went wrong. Please try again.";
};

/**
 * Drives the "Verify number" flow: saves the typed number if it differs from
 * the saved one (the server only texts the saved number), requests a code, and
 * confirms it. `typedPhone` is the live value of the phone input.
 */
const usePhoneVerification = ({ userId, userDetails, typedPhone, getTypedPhone }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState("idle"); // idle | code
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const savedPhone = userDetails?.phoneNumber || "";
  const typedE164 = typedPhone ? toE164(typedPhone) : null;
  const verified = Boolean(userDetails?.phoneVerified) && (!typedE164 || typedE164 === savedPhone);

  // Editing the number invalidates any code that was sent to the old one.
  useEffect(() => {
    if (step === "code" && typedE164 !== savedPhone) setStep("idle");
  }, [typedE164, savedPhone, step]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = useCallback(async () => {
    // Read the form at click time: the watched value can lag, and the saved
    // (Redux) user can be stale or missing the number the field is showing.
    const phone = getTypedPhone?.() || typedPhone;
    const e164 = phone ? toE164(phone) : null;
    if (!phone || !isValidPhone(phone)) {
      toast.error(phone ? PHONE_ERROR : ERROR_COPY.no_phone);
      return;
    }
    setBusy(true);
    try {
      // Always save the number the user sees before asking the server to text
      // it — the server only texts its own saved copy. A no-op if unchanged.
      const res = await PUT_USER(userId, { phoneNumber: e164 });
      const { password, ...saved } = res.data?._doc ?? res.data;
      dispatch(updateUserDetails(saved));
      await SEND_PHONE_VERIFICATION_CODE();
      setStep("code");
      setCooldown(RESEND_SECONDS);
      toast.success("Verification code sent — check your phone.");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.retryAfterSec) setCooldown(data.retryAfterSec);
      // A cooldown means a code is already out there, so let them enter it.
      if (data?.error === "cooldown") setStep("code");
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [typedPhone, getTypedPhone, userId, dispatch]);

  const confirmCode = useCallback(
    async (code) => {
      setBusy(true);
      try {
        await CONFIRM_PHONE_VERIFICATION_CODE(code);
        dispatch(updateUserDetails({ phoneVerified: true }));
        setStep("idle");
        toast.success("Phone number verified.");
        return true;
      } catch (err) {
        toast.error(errorMessage(err));
        if (["expired", "too_many_attempts", "no_code"].includes(err?.response?.data?.error)) {
          setStep("idle");
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [dispatch],
  );

  return { step, busy, cooldown, verified, sendCode, confirmCode, cancel: () => setStep("idle") };
};

export default usePhoneVerification;
